import {
  CosmosQueryFileSchema,
  CosmosQueryResultItemSchema,
  CosmosSourceSchema,
} from "@api/generated/types.gen";

import { stripBundlePrefix } from "@utils/bundle-name";

// The backend answer can carry inline [n] citation markers (e.g. "ROM is
// normal [2]."), where each n maps to a numbered entry in `sources`. We resolve
// those markers to interactive citation chips.
//
// When the answer has no markers (older backend, or a response without
// citation data), we fall back to a best-effort approximation: link any source
// file name the model happens to write verbatim into the answer. That keeps
// chips working across the rollout while the marker-emitting backend ships.

export type AnswerSegment =
  | { type: "text"; text: string }
  | {
      type: "citation";
      label: string;
      file?: CosmosQueryFileSchema;
      page: number;
      /**
       * The cited passage text, used to highlight the sourcing content in the
       * doc preview (MDRS-134). Only present on the marker path, where
       * `sources[]` carries it; absent on the name-fallback path.
       */
      snippet?: string;
      /**
       * Exact highlight boxes for the cited passage, normalized 0-1 (MDRS-134).
       * Present when the backend supplied OCR layout for the chunk; the preview
       * draws these directly and skips snippet matching.
       */
      bboxes?: CosmosSourceSchema["bboxes"];
    };

// Matches one or more adjacent citation markers, e.g. "[2]" or "[2][3]".
const MARKER_PATTERN = /(\[\d+\](?:\s*\[\d+\])*)/g;
// Pulls each individual number out of a marker run.
const MARKER_NUMBER_PATTERN = /\[(\d+)\]/g;

const escapeRegExp = (value: string): string =>
  value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

type SourceName = {
  name: string;
  file: CosmosQueryFileSchema;
  page: number;
};

// Build the candidate names (raw + bundle-stripped) for each result, longest
// first so a more specific name wins over a shorter substring.
const collectNames = (results: CosmosQueryResultItemSchema[]): SourceName[] => {
  const names: SourceName[] = [];
  for (const result of results) {
    if (!result.file?.name) continue;
    const page = result.pages?.[0] ?? 1;
    const candidates = new Set(
      [result.file.name, stripBundlePrefix(result.file.name)].filter(Boolean)
    );
    for (const name of candidates) {
      names.push({ name, file: result.file, page });
    }
  }
  return names.sort((a, b) => b.name.length - a.name.length);
};

// Display label for a source chip, e.g. "GP_Summary p.3".
const sourceLabel = (source: CosmosSourceSchema): string => {
  const name = stripBundlePrefix(source.document_name) || source.document_name;
  return source.page != null ? `${name} p.${source.page}` : name;
};

// Marker-based linking: split the text on [n] runs and resolve each number to
// its `sources` entry. The matching `results` item (by document name) supplies
// the file object so the chip can open the source.
const linkByMarkers = (
  text: string,
  sources: CosmosSourceSchema[],
  results: CosmosQueryResultItemSchema[]
): AnswerSegment[] => {
  const sourceByIndex = new Map(sources.map((s) => [s.index, s]));
  const fileByName = new Map<string, CosmosQueryFileSchema>();
  for (const result of results) {
    if (result.file?.name) fileByName.set(result.file.name, result.file);
  }

  const segments: AnswerSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  MARKER_PATTERN.lastIndex = 0;
  while ((match = MARKER_PATTERN.exec(text)) !== null) {
    const run = match[0];
    if (match.index > lastIndex) {
      segments.push({ type: "text", text: text.slice(lastIndex, match.index) });
    }

    // Expand a run like "[2][3]" into one chip per resolvable source.
    const numbers = [...run.matchAll(MARKER_NUMBER_PATTERN)].map((m) =>
      Number(m[1])
    );
    for (const n of numbers) {
      const source = sourceByIndex.get(n);
      if (!source) continue; // unknown marker number — drop it
      segments.push({
        type: "citation",
        label: sourceLabel(source),
        file: fileByName.get(source.document_name),
        page: source.page ?? 1,
        // Only carry a non-empty snippet; the preview treats absence as
        // "no highlight" and just jumps to the page.
        ...(source.snippet ? { snippet: source.snippet } : {}),
        // Exact boxes when the backend supplied them (MDRS-134).
        ...(source.bboxes && source.bboxes.length > 0
          ? { bboxes: source.bboxes }
          : {}),
      });
    }

    lastIndex = match.index + run.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", text: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", text }];
};

// Fallback linking: match source file names written verbatim in the prose.
const linkByNames = (
  text: string,
  results: CosmosQueryResultItemSchema[]
): AnswerSegment[] => {
  const names = collectNames(results);
  if (names.length === 0) return [{ type: "text", text }];

  const pattern = new RegExp(names.map((n) => escapeRegExp(n.name)).join("|"), "g");

  const segments: AnswerSegment[] = [];
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    const matched = match[0];
    const source = names.find((n) => n.name === matched);
    if (!source) continue;

    if (match.index > lastIndex) {
      segments.push({ type: "text", text: text.slice(lastIndex, match.index) });
    }
    segments.push({
      type: "citation",
      label: matched,
      file: source.file,
      page: source.page,
    });
    lastIndex = match.index + matched.length;
  }

  if (lastIndex < text.length) {
    segments.push({ type: "text", text: text.slice(lastIndex) });
  }

  return segments.length > 0 ? segments : [{ type: "text", text }];
};

/**
 * Split answer text into plain-text and citation segments.
 *
 * Prefers inline [n] markers resolved against `sources`; falls back to
 * matching source file names verbatim when the answer carries no markers.
 */
export const linkSources = (
  text: string,
  results: CosmosQueryResultItemSchema[],
  sources: CosmosSourceSchema[] = []
): AnswerSegment[] => {
  const hasMarkers = sources.length > 0 && MARKER_PATTERN.test(text);
  MARKER_PATTERN.lastIndex = 0; // reset after the stateful .test() above

  if (hasMarkers) return linkByMarkers(text, sources, results);
  return linkByNames(text, results);
};
